/**
 * `<PointCloudToolCard />` — WP-5.
 *
 * Right-rail tool card for point-cloud layers. Activates when the
 * `useViewerStore.sceneSelection.toolCardKind === 'pointcloud'` and / or
 * when the page receives a `viewer:tool-card-request` `CustomEvent` from
 * the scene tree (WP-2 contract).
 *
 * Surface contract: the card is a controlled view that delegates engine
 * mutations to the supplied adapter ("Giro3D" today; "Potree" wired with a
 * compatible API for the multi-engine path).
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §8.5
 *     and §7.5 Card 5 — point-cloud tool requirements.
 *   - §9 P0 — shared `<CoordinateModeBadge />` consumed here and in Card 6.
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 — tokens
 *     only, single primary CTA per row, 40 px touch min, focus visible,
 *     non-color cue per state.
 */

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  Banner,
  Button,
  Chip,
  Icon,
  IconButton,
  tokens,
  type ChipVariant,
} from '@/design';
import {
  TOOL_CARD_REQUEST_EVENT,
  type ToolCardRequestDetail,
} from '@/app/sceneSelection';
import { useViewerStore } from '@/state/useViewerStore';
import {
  CoordinateModeBadge,
  coordinateModeForLayer,
  type CoordinateMode,
} from '@/components/three-d/CoordinateModeBadge';
import type { PotreeBackendStatus } from '@/engines/potree/PotreeAdapter';
import type {
  PointCloudColorMode,
  PointCloudQualitySettings,
  PointCloudQualitySnapshot,
} from '@/engines/giro3d/Giro3DAdapter';
import {
  PointCloudQualityPanel,
  POINT_SIZE_MIN,
  POINT_SIZE_MAX,
  POINT_SIZE_STEP,
  nextColorMode,
} from './PointCloudQualityPanel';
import { useMeasureTool, type MeasureMode } from '@/engines/giro3d/useMeasureTool';
import type { MeasureSurfaces } from '@/engines/giro3d/measureController';
import {
  SectionAndClipToolCard,
  type SectionAndClipCommandAdapter,
} from '@/components/three-d/SectionAndClipToolCard';

// ---------------------------------------------------------------------------
// Adapter contract (narrowed so both Giro3D and Potree can satisfy it)
// ---------------------------------------------------------------------------

/**
 * Minimal point-cloud adapter contract consumed by the ToolCard. Both
 * `Giro3DAdapter` and `PotreeAdapter` implement this surface. Keeping the
 * type local lets us mock the adapter in tests without dragging in the
 * full engine modules.
 */
export interface PointCloudCommandAdapter {
  getPointCloudQuality(): PointCloudQualitySnapshot | null;
  setPointCloudQuality(settings: Partial<PointCloudQualitySettings>): void;
  setPointCloudClassificationFilter?(classes: ReadonlySet<number>): void;
  getPointCloudClassificationFilter?(): ReadonlySet<number>;
  fitToPointClouds?(): void;
  /** Optional: exposes Giro3D scene/camera/domEl for 3D measurement raycasting. */
  getMeasureSurfaces?(): MeasureSurfaces | null;
  /** Optional: exposes section-plane and clipping-box control. */
  getSectionAndClipAdapter?(): SectionAndClipCommandAdapter | null;
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface PointCloudToolCardProps {
  /** Engine adapter (Giro3D today). Null when no 3D engine is active. */
  adapter: PointCloudCommandAdapter | null;
  /** Selected layer (when known). Drives the local-coordinate banner and
   *  the source-status badge. */
  layer?: Layer | null;
  /** Backend status from the optional `PotreeAdapter` runtime, when active. */
  potreeStatus?: PotreeBackendStatus;
  /** Override for the snapshot polling cadence in ms (tests). */
  pollIntervalMs?: number;
  /** Optional close handler — when omitted the card stays mounted but
   *  collapses on a 'cleared' selection. */
  onClose?: () => void;
  /** When true, ignore the selection contract and always render. Useful for
   *  story books and the in-canvas Giro3D toolbar embed. */
  forceVisible?: boolean;
  /** Optional initial snapshot — used for SSR / tests. */
  initialQuality?: PointCloudQualitySnapshot;
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PointCloudToolCard({
  adapter,
  layer,
  potreeStatus,
  pollIntervalMs = 1500,
  onClose,
  forceVisible = false,
  initialQuality,
  'data-testid': testId,
}: PointCloudToolCardProps) {
  // Measurement — wired to Giro3D raycaster when the adapter exposes getMeasureSurfaces
  const getSurfaces = useCallback(
    (): MeasureSurfaces | null => adapter?.getMeasureSurfaces?.() ?? null,
    [adapter],
  );
  const measure = useMeasureTool(getSurfaces);
  const sectionAdapter = adapter?.getSectionAndClipAdapter?.() ?? null;

  const sceneSelectionFromHook = useViewerStore((s) => s.sceneSelection);
  // SSR-safe read: in `renderToStaticMarkup` Zustand's
  // `useSyncExternalStore` server snapshot may not reflect imperative
  // `setState` mutations applied immediately before render (see
  // `SavedViewsPanel.tsx` for the same pattern). Read the live snapshot at
  // render and prefer it when the hook value is the cleared sentinel.
  const liveSceneSelection = useViewerStore.getState().sceneSelection;
  const sceneSelection =
    sceneSelectionFromHook.kind === 'cleared'
      ? liveSceneSelection
      : sceneSelectionFromHook;
  const [eventToolCardKind, setEventToolCardKind] = useState<string | null>(
    null,
  );
  const [quality, setQuality] = useState<PointCloudQualitySnapshot | null>(
    () => initialQuality ?? null,
  );
  const [classificationFilter, setClassificationFilter] = useState<
    ReadonlySet<number>
  >(() => {
    if (adapter?.getPointCloudClassificationFilter) {
      return new Set(adapter.getPointCloudClassificationFilter());
    }
    return new Set<number>();
  });

  // Subscribe to the WP-2 `viewer:tool-card-request` custom event so the
  // card can activate even before the store is updated (e.g. the scene-tree
  // dispatches the event synchronously alongside the store write).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ToolCardRequestDetail>).detail;
      if (!detail) return;
      setEventToolCardKind(detail.toolCardKind);
    };
    window.addEventListener(TOOL_CARD_REQUEST_EVENT, handler);
    return () => window.removeEventListener(TOOL_CARD_REQUEST_EVENT, handler);
  }, []);

  // Resolve whether the card should be visible. Either contract activates
  // the card; this matches the WP-5 brief.
  const selectionActivates =
    sceneSelection.toolCardKind === 'pointcloud' ||
    sceneSelection.layerKind === 'pointcloud';
  const eventActivates = eventToolCardKind === 'pointcloud';
  const visible = forceVisible || selectionActivates || eventActivates;

  // Poll the adapter for the live quality snapshot whenever the card is
  // visible and an adapter is wired. The cadence matches the Giro3D
  // toolbar's existing internal polling (no double-pump in production).
  const refreshQuality = useCallback(() => {
    if (!adapter) {
      setQuality(initialQuality ?? null);
      return;
    }
    setQuality(adapter.getPointCloudQuality());
    if (adapter.getPointCloudClassificationFilter) {
      setClassificationFilter(
        new Set(adapter.getPointCloudClassificationFilter()),
      );
    }
  }, [adapter, initialQuality]);

  useEffect(() => {
    if (!visible) return;
    refreshQuality();
    if (pollIntervalMs <= 0 || typeof window === 'undefined') return;
    const handle = window.setInterval(refreshQuality, pollIntervalMs);
    return () => window.clearInterval(handle);
  }, [visible, refreshQuality, pollIntervalMs]);

  const handlePatch = useCallback(
    (next: Partial<PointCloudQualitySettings>) => {
      applyQualityPatch(adapter, next);
      refreshQuality();
    },
    [adapter, refreshQuality],
  );

  const handleToggleClassification = useCallback(
    (classCode: number) => {
      const next = applyClassificationToggle(
        adapter,
        classificationFilter,
        classCode,
      );
      setClassificationFilter(next);
    },
    [adapter, classificationFilter],
  );

  const handleClearClassification = useCallback(() => {
    adapter?.setPointCloudClassificationFilter?.(new Set());
    setClassificationFilter(new Set());
  }, [adapter]);

  const handleCycleColorMode = useCallback(() => {
    cycleColorMode(adapter, quality?.colorMode ?? 'rgb');
    refreshQuality();
  }, [adapter, quality, refreshQuality]);

  const handleFitToCloud = useCallback(() => {
    adapter?.fitToPointClouds?.();
  }, [adapter]);

  // Keyboard shortcuts: `[` decreases point size, `]` increases. Bounded by
  // the panel's documented min / max. Only bound while visible and a
  // snapshot is known.
  useEffect(() => {
    if (!visible || !quality || typeof window === 'undefined') return;
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target && isTypingInForm(target)) return;
      if (event.key === '[') {
        event.preventDefault();
        adjustPointSize(adapter, quality.pointSize, 'decrease');
        refreshQuality();
      } else if (event.key === ']') {
        event.preventDefault();
        adjustPointSize(adapter, quality.pointSize, 'increase');
        refreshQuality();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, quality, adapter, refreshQuality]);

  if (!visible) return null;

  const coordinateMode: CoordinateMode = coordinateModeForLayer(layer ?? null);
  const sourceStatus = computeSourceStatus(layer ?? null, potreeStatus);

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[3],
    background: tokens.color.surface[2],
    border: tokens.border.thin,
    borderRadius: tokens.radius.lg,
    padding: tokens.space[3],
    width: tokens.width.panelMd,
    boxShadow: tokens.elevation[1],
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.space[2],
  };

  const titleStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.lg,
    fontWeight: tokens.font.weight.semibold,
  };

  const subtitleStyle: CSSProperties = {
    color: tokens.color.ink.secondary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.body.size,
  };

  return (
    <section
      aria-label="Point cloud tool card"
      data-testid={testId ?? 'point-cloud-tool-card'}
      style={containerStyle}
    >
      <header style={headerStyle}>
        <div style={titleStyle}>
          <Icon name="blur_on" size={24} decorative />
          <span>Point cloud</span>
        </div>
        {onClose ? (
          <IconButton aria-label="Close point cloud tool card" onClick={onClose}>
            <Icon name="close" size={20} decorative />
          </IconButton>
        ) : null}
      </header>

      <div style={subtitleStyle}>
        {layer?.label ?? layer?.id ?? 'Select a point-cloud layer to begin.'}
      </div>

      <div
        role="group"
        aria-label="Point cloud status"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.space[1],
          alignItems: 'center',
        }}
      >
        <CoordinateModeBadge mode={coordinateMode} context={layer?.label} />
        <Chip
          icon={sourceStatus.icon}
          label={sourceStatus.label}
          variant={sourceStatus.variant}
          aria-label={`Source status: ${sourceStatus.label}`}
          data-testid={`point-cloud-source-status-${sourceStatus.id}`}
        />
        <Chip
          icon="palette"
          label={`Color: ${currentColorLabel(quality)}`}
          variant="neutral"
          pressable
          onClick={handleCycleColorMode}
          aria-label={`Cycle point cloud color mode — current ${currentColorLabel(quality)}`}
          data-testid="point-cloud-color-cycle"
        />
      </div>

      {coordinateMode === 'local' ? (
        <Banner
          severity="warning"
          title="Local coordinate frame"
          body={
            <span>
              This point cloud has no real-world projection
              {layer?.label ? ` (${layer.label})` : ''}.
              Measurements and overlays will use the cloud&rsquo;s local
              axes; map-aligned tools are unavailable until a CRS is
              registered.
            </span>
          }
          narrationKey={`pcc-local-${layer?.id ?? 'unknown'}`}
          data-testid="point-cloud-local-frame-banner"
        />
      ) : null}

      {sourceStatus.id === 'not-installed' ? (
        <Banner
          severity="info"
          title="Potree runtime not installed"
          body="The Potree backend package is not bundled. The Giro3D adapter handles COPC clouds; install the Potree runtime to add Potree-v2 octree support."
          narrationKey="pcc-potree-not-installed"
          data-testid="point-cloud-potree-not-installed-banner"
        />
      ) : null}

      {quality ? (
        <PointCloudQualityPanel
          quality={quality}
          onChange={handlePatch}
          classificationFilter={classificationFilter}
          onToggleClassification={handleToggleClassification}
          onClearClassificationFilter={handleClearClassification}
          embedded
        />
      ) : (
        <Banner
          severity="info"
          title="No point-cloud layers loaded"
          body="Add a COPC or Potree-v2 layer to the project to enable quality, color, and classification controls."
          narrationKey="pcc-no-layers"
          data-testid="point-cloud-empty-banner"
        />
      )}

      <footer
        style={{
          display: 'flex',
          gap: tokens.space[2],
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        {adapter?.fitToPointClouds ? (
          <Button
            variant="secondary"
            onClick={handleFitToCloud}
            data-testid="point-cloud-fit-button"
            aria-label="Fit camera to point cloud bounds"
          >
            <Icon name="center_focus_strong" size={20} decorative /> Fit to cloud
          </Button>
        ) : null}
      </footer>

      {/* ── Measure ──────────────────────────────────────────────── */}
      <div role="group" aria-label="Measurement tools">
        <div style={sectionHeadingStyle}>
          <Icon name="straighten" size={16} decorative />
          <span>Measure</span>
        </div>
        <div style={toolRowStyle}>
          <MeasureBtn label="Distance" icon="straighten" mode="distance" active={measure.mode} onClick={measure.start} />
          <MeasureBtn label="Height" icon="height" mode="height" active={measure.mode} onClick={measure.start} />
          <MeasureBtn label="Point (XYZ)" icon="location_on" mode="point" active={measure.mode} onClick={measure.start} />
          {measure.results.length > 0 ? (
            <button
              type="button"
              onClick={measure.clear}
              aria-label="Clear all measurements"
              style={clearBtnStyle}
            >
              <Icon name="clear_all" size={16} decorative />Clear
            </button>
          ) : null}
        </div>
        {measure.mode !== 'none' ? (
          <p style={measureHintStyle}>
            {measure.mode === 'point'
              ? 'Click a point on the cloud to record its XYZ coordinate'
              : measure.pointsCollected === 0
                ? 'Click the first point on the cloud'
                : 'Click the second point to complete measurement'}
          </p>
        ) : null}
        {measure.results.length > 0 ? (
          <ul style={measureListStyle} aria-label="Measurement results">
            {measure.results.map((r, i) => (
              <li key={`${r.type}-${i}`} style={measureRowStyle}>
                <span style={measureTypeStyle}>{r.type}</span>
                <span style={measureValueStyle}>{r.value}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* ── Section / Clip ───────────────────────────────────────── */}
      {sectionAdapter ? (
        <div role="group" aria-label="Section and clip tools">
          <div style={sectionHeadingStyle}>
            <Icon name="content_cut" size={16} decorative />
            <span>Section &amp; Clip</span>
          </div>
          <SectionAndClipToolCard adapter={sectionAdapter} forceVisible />
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// MeasureBtn — compact icon+label button for the measure toolbar row
// ---------------------------------------------------------------------------

interface MeasureBtnProps {
  readonly label: string;
  readonly icon: Parameters<typeof Icon>[0]['name'];
  readonly mode: MeasureMode;
  readonly active: MeasureMode;
  readonly onClick: (mode: MeasureMode) => void;
}

function MeasureBtn({ label, icon, mode, active, onClick }: MeasureBtnProps) {
  const isActive = active === mode;
  return (
    <button
      type="button"
      aria-pressed={isActive}
      aria-label={`${label} measurement tool`}
      onClick={() => onClick(mode)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        minHeight: tokens.density.touchMin,
        paddingInline: tokens.space[2],
        border: tokens.border.thin,
        borderRadius: tokens.radius.input,
        background: isActive ? tokens.color.accent.primary : tokens.color.surface[1],
        color: isActive ? tokens.color.accent.on : tokens.color.ink.primary,
        cursor: 'pointer',
        fontSize: tokens.font.size.sm,
        fontWeight: isActive ? tokens.font.weight.medium : tokens.font.weight.regular,
      }}
    >
      <Icon name={icon} size={16} decorative />
      {label}
    </button>
  );
}

// Sub-section heading style shared between Measure and Section/Clip blocks
const sectionHeadingStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[1],
  fontSize: tokens.font.size.xs,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: tokens.color.ink.muted,
  marginBottom: tokens.space[2],
  marginTop: tokens.space[3],
};

const toolRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.space[1],
};

const clearBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[1],
  minHeight: tokens.density.touchMin,
  paddingInline: tokens.space[2],
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: 'transparent',
  color: tokens.color.semantic.danger,
  cursor: 'pointer',
  fontSize: tokens.font.size.sm,
};

const measureHintStyle: CSSProperties = {
  margin: `${tokens.space[2]}px 0 0`,
  fontSize: tokens.font.size.xs,
  color: tokens.color.accent.primary,
};

const measureListStyle: CSSProperties = {
  listStyle: 'none',
  margin: `${tokens.space[2]}px 0 0`,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
};

const measureRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  borderRadius: tokens.radius.input,
  background: tokens.color.surface[1],
  fontSize: tokens.font.size.sm,
};

const measureTypeStyle: CSSProperties = {
  color: tokens.color.ink.secondary,
  textTransform: 'capitalize',
};

const measureValueStyle: CSSProperties = {
  fontFamily: tokens.font.family.mono,
  color: tokens.color.ink.primary,
};

// ---------------------------------------------------------------------------
// Helpers (pure) — exported for test-driven verification of the adapter
// round-trip per WP-5 acceptance §8.5.
// ---------------------------------------------------------------------------

export function toggleClass(
  filter: ReadonlySet<number>,
  classCode: number,
): ReadonlySet<number> {
  const next = new Set(filter);
  if (next.has(classCode)) next.delete(classCode);
  else next.add(classCode);
  return next;
}

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Apply a quality patch through the adapter — single ingress for every
 * mutation the card performs.  Returning `true` lets tests assert the
 * adapter received the call; `false` means the adapter was null.
 */
export function applyQualityPatch(
  adapter: PointCloudCommandAdapter | null,
  patch: Partial<PointCloudQualitySettings>,
): boolean {
  if (!adapter) return false;
  adapter.setPointCloudQuality(patch);
  return true;
}

/** Adjust point size via keyboard. Bounded to the panel's range. */
export function adjustPointSize(
  adapter: PointCloudCommandAdapter | null,
  current: number,
  direction: 'increase' | 'decrease',
): number {
  const delta = direction === 'increase' ? POINT_SIZE_STEP : -POINT_SIZE_STEP;
  const next = clamp(current + delta, POINT_SIZE_MIN, POINT_SIZE_MAX);
  applyQualityPatch(adapter, { pointSize: next });
  return next;
}

/** Cycle the color mode through the documented RGB → elevation → … chain. */
export function cycleColorMode(
  adapter: PointCloudCommandAdapter | null,
  current: PointCloudColorMode,
): PointCloudColorMode {
  const next = nextColorMode(current);
  applyQualityPatch(adapter, { colorMode: next });
  return next;
}

/**
 * Apply a classification chip toggle through the adapter. When the adapter
 * has no classification filter API the call is a no-op for the engine but
 * the helper still returns the next UI mirror so the ToolCard stays
 * responsive in environments without a real engine (storybooks, tests).
 */
export function applyClassificationToggle(
  adapter: PointCloudCommandAdapter | null,
  current: ReadonlySet<number>,
  classCode: number,
): ReadonlySet<number> {
  const next = toggleClass(current, classCode);
  adapter?.setPointCloudClassificationFilter?.(next);
  return next;
}

function currentColorLabel(
  quality: PointCloudQualitySnapshot | null,
): string {
  const mode: PointCloudColorMode = quality?.colorMode ?? 'auto';
  switch (mode) {
    case 'rgb':
      return 'RGB';
    case 'elevation':
      return 'Elevation';
    case 'intensity':
      return 'Intensity';
    case 'classification':
      return 'Class';
    case 'auto':
    default:
      return 'Auto';
  }
}

interface SourceStatus {
  readonly id: 'loaded' | 'partial' | 'failed' | 'not-installed';
  readonly label: string;
  readonly icon: 'check_circle' | 'sync_problem' | 'error' | 'extension_off';
  readonly variant: ChipVariant;
}

function computeSourceStatus(
  layer: Layer | null,
  potreeStatus: PotreeBackendStatus | undefined,
): SourceStatus {
  if (potreeStatus === 'not-installed' && layer?.endpoints.potree) {
    return {
      id: 'not-installed',
      label: 'Potree not installed',
      icon: 'extension_off',
      variant: 'warning',
    };
  }
  if (potreeStatus === 'failed-to-load' || potreeStatus === 'failed-during-render') {
    return {
      id: 'failed',
      label: 'Source failed',
      icon: 'error',
      variant: 'error',
    };
  }
  if (potreeStatus === 'loading') {
    return {
      id: 'partial',
      label: 'Loading tiles',
      icon: 'sync_problem',
      variant: 'info',
    };
  }
  return {
    id: 'loaded',
    label: 'Source loaded',
    icon: 'check_circle',
    variant: 'success',
  };
}

function isTypingInForm(target: HTMLElement): boolean {
  const tag = target.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}
